import axiosInstance from './axios.service';

/**
 * @typedef {Object} GoodsDetailedListBody
 * @property {string} [pointId] - Id точки (20 символов, a-zA-Z0-9)
 * @property {string|null} [departmentId] - Id департамента (20 символов, nullable)
 * @property {string[]} [productIds] - Массив ID продуктов
 * @property {string} [sku] - Артикул
 * @property {string} [type] - Тип (например, "modifier", "product")
 * @property {boolean} [isVisible] - Флаг видимости
 * @property {boolean} [deleted] - Флаг удаления
 */

const validateGoodsRequestBody = (body = {}) => {
  const idPattern = /^[A-Za-z0-9]{20}$/;

  if (body.pointId && !idPattern.test(body.pointId)) {
    throw new Error('Invalid pointId: must be exactly 20 alphanumeric characters');
  }

  if (body.departmentId !== undefined && body.departmentId !== null && !idPattern.test(body.departmentId)) {
    throw new Error('Invalid departmentId: must be exactly 20 alphanumeric characters');
  }

  if (body.productIds !== undefined && !Array.isArray(body.productIds)) {
    throw new Error('Invalid productIds: must be an array');
  }

  if (Array.isArray(body.productIds) && body.productIds.some((item) => typeof item !== 'string')) {
    throw new Error('Invalid productIds: each item must be a string');
  }

  if (body.sku !== undefined && typeof body.sku !== 'string') {
    throw new Error('Invalid sku: must be a string');
  }

  if (body.type !== undefined && typeof body.type !== 'string') {
    throw new Error('Invalid type: must be a string');
  }

  if (body.isVisible !== undefined && typeof body.isVisible !== 'boolean') {
    throw new Error('Invalid isVisible: must be a boolean');
  }

  if (body.deleted !== undefined && typeof body.deleted !== 'boolean') {
    throw new Error('Invalid deleted: must be a boolean');
  }
};

/**
 * @typedef {Object} GoodsItem
 * @property {string} id - Уникальный идентификатор
 * @property {string} merchantId - ID мерчанта
 * @property {string} pointId - ID точки
 * @property {string|null} departmentId - ID департамента
 * @property {string|null} externalId - Внешний ID (например, из системы учета)
 * @property {string} type - Тип товара (например, "modifier", "product")
 * @property {string|number|null} defaultSize - Размер по умолчанию
 * @property {string|null} sku - Артикул
 * @property {string} name - Название товара
 * @property {string} description - Описание товара
 * @property {string|null} imgUrl - URL изображения
 * @property {number} sortNum - Порядковый номер для сортировки
 * @property {any[]|null} chips - Метки/чипсы товара
 * @property {string|number} price - Цена товара
 * @property {number|null} step - Шаг изменения количества
 * @property {string|null} unit - Единица измерения
 * @property {string|null} categoryId - ID категории
 * @property {boolean} isVisible - Флаг видимости
 * @property {boolean} deleted - Флаг удаления
 * @property {string} createdAt - Timestamp создания
 * @property {string|null} updatedAt - Timestamp обновления
 */

/**
 * @typedef {Object} GoodsDetailedListResponse
 * @property {boolean} success - Успешность запроса
 * @property {GoodsItem[]} data - Массив товаров
 */

/**
 * Получить детальный список товаров
 * @param {GoodsDetailedListBody} data
 * @param {Object} headers
 * @returns {Promise<GoodsDetailedListResponse>}
 */
export const getDetailedGoodsList = async (data, headers = {}) => {
  validateGoodsRequestBody(data);
  try {
    const url = '/operator/v1/goods/detailed-list';
    const response = await axiosInstance.post(url, data, { headers });
    return response;
  } catch (error) {
    console.error('Error in getDetailedGoodsList:', error);
    throw error;
  }
};

/**
 * @typedef {Object} GoodsReadBody
 * @property {string} [pointId] - Id точки
 * @property {string|null} [departmentId] - Id департамента
 * @property {string[]} [productIds] - Массив ID товаров
 * @property {string} [sku] - Артикул товара
 * @property {string} [type] - Тип (например, "modifier", "product")
 * @property {boolean} [isVisible] - Флаг видимости
 * @property {boolean} [deleted] - Флаг удаления
 */

/**
 * Получить товары с деталями модификаторов, групп и размеров
 * @param {GoodsReadBody} data
 * @param {Object} headers
 * @returns {Promise<{success: boolean, data: Array}>}
 */
export const getGoodsRead = async (data = {}, headers = {}) => {
  validateGoodsRequestBody(data);
  try {
    const url = '/operator/v1/goods/read';
    const payload = {
      ...(data || {}),
      showHidden: true,
    };
    const response = await axiosInstance.post(url, payload, { headers });
    return response;
  } catch (error) {
    console.error('Error in getGoodsRead:', error);
    throw error;
  }
};
